import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, User, Globe, Tag } from 'lucide-react';

const Login = ({ onLogin, onSignupSuccess, initialMode = 'login', onForgotPassword, onBackToLogin, onSwitchToSignup, onSwitchToLogin }) => {
  const [isSignup, setIsSignup] = useState(initialMode === 'signup');
  const [showPassword, setShowPassword] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(initialMode === 'forgot');
  const [forgotStep, setForgotStep] = useState('email'); // 'email' | 'otp' | 'password'
  const [resetToken, setResetToken] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    confirmPassword: '',
    websiteAddress: '',
    discountCode: ''
  });
  const [forgotData, setForgotData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmNewPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [forgotMessage, setForgotMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleForgotInputChange = (e) => {
    const { name, value } = e.target;
    setForgotData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (isSignup) {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      if (!formData.websiteAddress) {
        newErrors.websiteAddress = 'Website address is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const endpoint = isSignup ? '/app/api/auth/signup.php' : '/app/api/auth/login.php';
      const payload = isSignup
        ? {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            website_address: formData.websiteAddress,
            discount_code: formData.discountCode,
            role: 'site_manager'
          }
        : {
            email: formData.email,
            password: formData.password
          };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (isSignup) {
          // Show thank you page instead of logging in
          if (onSignupSuccess) onSignupSuccess();
        } else {
          onLogin({
            id: data.user.id,
            name: data.user.name,
            email: data.user.email,
            role: data.user.role,
            token: data.token,
            plan: data.user.plan
          });
        }
      } else {
        setErrors({ submit: data.message || 'Authentication failed' });
      }
    } catch (error) {
      setErrors({ submit: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    if (!forgotData.email) {
      setErrors({ forgotEmail: 'Email is required' });
      return;
    }
    setLoading(true);
    setForgotMessage('');
    setErrors({});
    try {
      const response = await fetch('/app/api/auth/forgot-password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotData.email }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setForgotStep('otp');
        setForgotMessage('A reset code has been sent to your email address.');
      } else {
        setErrors({ forgotEmail: data.message || 'Failed to send reset code' });
      }
    } catch {
      setErrors({ forgotEmail: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!forgotData.otp) {
      setErrors({ forgotOtp: 'Reset code is required' });
      return;
    }
    setLoading(true);
    setForgotMessage('');
    setErrors({});
    try {
      const response = await fetch('/app/api/auth/verify-otp.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotData.email, otp_code: forgotData.otp }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setResetToken(data.reset_token);
        setForgotStep('password');
        setForgotMessage('');
      } else {
        setErrors({ forgotOtp: data.message || 'Invalid or expired reset code' });
      }
    } catch {
      setErrors({ forgotOtp: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordReset = async (e) => {
    e.preventDefault();
    if (!forgotData.newPassword || forgotData.newPassword.length < 6) {
      setErrors({ forgotNewPassword: 'Password must be at least 6 characters' });
      return;
    }
    if (forgotData.newPassword !== forgotData.confirmNewPassword) {
      setErrors({ forgotConfirmNewPassword: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    setForgotMessage('');
    setErrors({});
    try {
      const response = await fetch('/app/api/auth/reset-password.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: resetToken, new_password: forgotData.newPassword }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        onLogin({
          id: data.user.id,
          name: data.user.name,
          email: data.user.email,
          role: data.user.role,
          token: data.token,
          plan: data.user.plan
        });
      } else {
        setErrors({ forgotNewPassword: data.message || 'Failed to reset password' });
      }
    } catch {
      setErrors({ forgotNewPassword: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    const next = !isSignup;
    setIsSignup(next);
    setFormData({ email: '', password: '', name: '', confirmPassword: '', websiteAddress: '', discountCode: '' });
    setErrors({});
    if (next && onSwitchToSignup) onSwitchToSignup();
    if (!next && onSwitchToLogin) onSwitchToLogin();
  };

  if (isForgotPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ background: '#000' }}>
        <div className="max-w-md w-full space-y-8">
          <div>
            <div className="mx-auto flex items-center justify-center">
              <img src="https://mageguard.net/assets/logo.png" alt="MageGuard" className="h-12" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Reset your password
            </h2>
            <p className="mt-2 text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <button
                onClick={() => { setIsForgotPassword(false); setForgotStep('email'); setForgotData({ email: '', otp: '', newPassword: '', confirmNewPassword: '' }); setResetToken(''); setForgotMessage(''); setErrors({}); if (onBackToLogin) onBackToLogin(); }}
                className="font-medium" style={{ color: '#d4922a' }}
              >
                Back to sign in
              </button>
            </p>
          </div>

          {forgotMessage && (
            <div className="rounded-md p-4" style={{ background: 'rgba(212,146,42,0.1)', border: '1px solid rgba(212,146,42,0.3)' }}>
              <div className="text-sm" style={{ color: '#d4922a' }}>{forgotMessage}</div>
            </div>
          )}

          {forgotStep === 'email' && (
            <form className="mt-8 space-y-6" onSubmit={handleForgotPasswordRequest}>
              <div>
                <label htmlFor="forgotEmail" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  Email Address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <input
                    id="forgotEmail"
                    name="email"
                    type="email"
                    required
                    className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="Enter your email"
                    value={forgotData.email}
                    onChange={handleForgotInputChange}
                  />
                </div>
                {errors.forgotEmail && <p className="mt-1 text-sm text-red-400">{errors.forgotEmail}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border-0 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #d4922a 0%, #e8a840 30%, #fff 100%)', color: '#000' }}
              >
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div> : 'Send Reset Code'}
              </button>
            </form>
          )}

          {forgotStep === 'otp' && (
            <form className="mt-8 space-y-6" onSubmit={handleVerifyOtp}>
              <div>
                <label className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Reset Code</label>
                <input
                  name="otp"
                  type="text"
                  className="mt-1 appearance-none block w-full px-3 py-2 rounded-md focus:outline-none sm:text-sm"
                  style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                  placeholder="Enter the 6-digit code from your email"
                  value={forgotData.otp}
                  onChange={handleForgotInputChange}
                />
                {errors.forgotOtp && <p className="mt-1 text-sm text-red-400">{errors.forgotOtp}</p>}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border-0 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #d4922a 0%, #e8a840 30%, #fff 100%)', color: '#000' }}
              >
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div> : 'Verify Code'}
              </button>
            </form>
          )}

          {forgotStep === 'password' && (
            <form className="mt-8 space-y-6" onSubmit={handleForgotPasswordReset}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>New Password</label>
                  <input
                    name="newPassword"
                    type="password"
                    className="mt-1 appearance-none block w-full px-3 py-2 rounded-md focus:outline-none sm:text-sm"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="New password (min 6 characters)"
                    value={forgotData.newPassword}
                    onChange={handleForgotInputChange}
                  />
                  {errors.forgotNewPassword && <p className="mt-1 text-sm text-red-400">{errors.forgotNewPassword}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Confirm New Password</label>
                  <input
                    name="confirmNewPassword"
                    type="password"
                    className="mt-1 appearance-none block w-full px-3 py-2 rounded-md focus:outline-none sm:text-sm"
                    style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="Confirm new password"
                    value={forgotData.confirmNewPassword}
                    onChange={handleForgotInputChange}
                  />
                  {errors.forgotConfirmNewPassword && <p className="mt-1 text-sm text-red-400">{errors.forgotConfirmNewPassword}</p>}
                </div>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border-0 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #d4922a 0%, #e8a840 30%, #fff 100%)', color: '#000' }}
              >
                {loading ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div> : 'Reset Password & Sign In'}
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" style={{ background: '#000' }}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto flex items-center justify-center">
            <img src="https://mageguard.net/assets/logo.png" alt="MageGuard" className="h-12" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
            {isSignup ? 'Create your account' : 'Sign in to MageGuard'}
          </h2>
          <p className="mt-2 text-center text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {isSignup ? (
              <>
                Already have an account?{' '}
                <button onClick={toggleMode} className="font-medium" style={{ color: '#d4922a' }}>
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button onClick={toggleMode} className="font-medium" style={{ color: '#d4922a' }}>
                  Sign up as Site Manager
                </button>
              </>
            )}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {isSignup && (
              <div>
                <label htmlFor="name" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Full Name</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required={isSignup}
                    className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Email Address</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
            </div>

            {isSignup && (
              <div>
                <label htmlFor="websiteAddress" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Website Address</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <input
                    id="websiteAddress"
                    name="websiteAddress"
                    type="text"
                    required={isSignup}
                    className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="https://yourstore.com"
                    value={formData.websiteAddress}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.websiteAddress && <p className="mt-1 text-sm text-red-600">{errors.websiteAddress}</p>}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Password</label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className="appearance-none relative block w-full pl-10 pr-10 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} /> : <Eye className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {isSignup && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>Confirm Password</label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required={isSignup}
                    className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                  />
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            )}

            {isSignup && (
              <div>
                <label htmlFor="discountCode" className="block text-sm font-medium" style={{ color: 'rgba(255,255,255,0.8)' }}>
                  Discount Code <span className="font-normal" style={{ color: 'rgba(255,255,255,0.4)' }}>(optional)</span>
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Tag className="h-5 w-5" style={{ color: 'rgba(255,255,255,0.4)' }} />
                  </div>
                  <input
                    id="discountCode"
                    name="discountCode"
                    type="text"
                    className="appearance-none relative block w-full pl-10 pr-3 py-2 rounded-md focus:outline-none sm:text-sm" style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(212,146,42,0.3)', color: '#fff' }}
                    placeholder="Enter discount code if you have one"
                    value={formData.discountCode}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="rounded-md p-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <div className="text-sm text-red-400">{errors.submit}</div>
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border-0 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #d4922a 0%, #e8a840 30%, #fff 100%)', color: '#000' }}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
              ) : (
                isSignup ? 'Create Account' : 'Sign In'
              )}
            </button>
          </div>

          {!isSignup && (
            <div className="text-center">
              <button
                type="button"
                onClick={() => { setIsForgotPassword(true); setErrors({}); if (onForgotPassword) onForgotPassword(); }}
                className="text-sm" style={{ color: '#d4922a' }}
              >
                Forgot your password?
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
